<!-- Header -->
<div class="d-flex w-100 m-b-4 align-items-center" style="gap: 1.5rem; flex-wrap: wrap;">
    <app-date-navigation
        (selectedDateUpdated)="updateSelectedDate()"
        [dateNavigationOptions]="dateNavigationOptions">
    </app-date-navigation>

    <div class="search-container">
        <i class="las la-search search-icon"></i>
        <input type="text" class="form-control search-input" placeholder="Search employees..."
               (keyup)="applyFilter($event)" #searchInput>
    </div>

    <label class="d-flex align-items-center pointer" style="font-size: 1.5rem; color: #666; white-space: nowrap;">
        <input (change)="filterData()" [(ngModel)]="showArchived" type="checkbox" class="m-r-1">
        Show archived
    </label>

    <div class="d-flex align-items-center" style="margin-left: auto; gap: 1rem;">
        <button class="button button--primary d-flex align-items-center nowrap"
                (click)="runAiAnalysis()"
                [disabled]="aiAnalyzing || loadingData || !employeesWithFinancialData?.length">
            <i class="las la-robot m-r-1" *ngIf="!aiAnalyzing"></i>
            <i class="las la-spinner la-spin m-r-1" *ngIf="aiAnalyzing"></i>
            {{ aiAnalyzing ? 'Analyseren...' : 'AI Analyse' }}
        </button>

        <button class="button button--outline d-flex align-items-center nowrap"
                (click)="sendEmail()"
                [disabled]="sendingEmail || loadingData || !employeesWithFinancialData?.length">
            <i class="las la-envelope m-r-1" *ngIf="!sendingEmail"></i>
            <i class="las la-spinner la-spin m-r-1" *ngIf="sendingEmail"></i>
            {{ sendingEmail ? 'Sending...' : 'Send Email' }}
        </button>
    </div>
</div>

<!-- Loading -->
<div *ngIf="loadingData" class="financial-loading-screen m-b-4">
    <div class="financial-loading-screen__inner">
        <app-loading-message></app-loading-message>
        <p class="financial-loading-screen__text">Financiele data wordt geladen...</p>
    </div>
</div>

<!-- Summary boxes -->
<div class="d-flex m-b-4" style="gap: 2rem; flex-wrap: wrap;" *ngIf="!loadingData && employeesWithFinancialData?.length > 0">
    <div class="box">
        <label class="m-b-1">Employees</label>
        <div class="summary-value">{{ getActiveEmployeeCount() }}</div>
        <small class="color--muted">{{ getFreelanceCount() }} freelance · {{ getPayrollCount() }} payroll</small>
    </div>
    <div class="box">
        <label class="m-b-1">Avg. Margin</label>
        <div class="summary-value" [ngClass]="{
            'summary-value--success': getAvgMargin() >= 30,
            'summary-value--warning': getAvgMargin() >= 15 && getAvgMargin() < 30,
            'summary-value--danger': getAvgMargin() < 15
        }">{{ getAvgMargin() | number:'1.0-0' }}%</div>
    </div>
    <div class="box">
        <label class="m-b-1">Total Income</label>
        <div class="summary-value summary-value--primary">{{ getTotalIncome() | currency:'EUR':'symbol':'1.0-0' }}</div>
    </div>
    <div class="box">
        <label class="m-b-1">Total Costs</label>
        <div class="summary-value">{{ getTotalCosts() | currency:'EUR':'symbol':'1.0-0' }}</div>
    </div>
    <div class="box">
        <label class="m-b-1">Total Margin</label>
        <div class="summary-value" [ngClass]="{
            'summary-value--success': getTotalMarginAmount() >= 0,
            'summary-value--danger': getTotalMarginAmount() < 0
        }">{{ getTotalMarginAmount() | currency:'EUR':'symbol':'1.0-0' }}</div>
    </div>
</div>

<!-- AI Analysis card -->
<div class="ai-card m-b-4" *ngIf="aiAnalysisHtml || aiAnalyzing">
    <div class="ai-card__inner">
        <div class="ai-card__header">
            <span class="ai-card__icon"><i class="las la-robot"></i></span>
            <span *ngIf="aiAnalyzing" class="ai-card__placeholder">
                <i class="las la-spinner la-spin"></i> Analyse wordt uitgevoerd...
            </span>
            <span *ngIf="aiAnalysisHtml" class="ai-card__title">AI Analyse</span>
        </div>
        <div class="ai-card__content" *ngIf="aiAnalysisHtml" [innerHTML]="aiAnalysisHtml"></div>
    </div>
</div>

<!-- Employee table -->
<div class="table__scroll" *ngIf="!loadingData">
    <table #sort="matSort" [dataSource]="datasource" mat-table matSort
           matSortActive="margin" matSortDirection="desc" class="table-view table-view--compact">

        <ng-container matColumnDef="employee">
            <th *matHeaderCellDef mat-header-cell mat-sort-header>Employee</th>
            <td *matCellDef="let row" mat-cell>
                <div class="d-flex align-items-center" style="gap: 1rem;">
                    <app-pill-employee [employeeId]="row.objectId" [onFinancialPage]="true"></app-pill-employee>
                    <span class="type-badge" [ngClass]="getEmployeeTypeBadgeClass(row.objectId)">
                        {{ getEmployeeTypeLabel(row.objectId) }}
                    </span>
                </div>
            </td>
        </ng-container>

        <ng-container matColumnDef="margin">
            <th *matHeaderCellDef mat-header-cell mat-sort-header>Margin %</th>
            <td *matCellDef="let row" mat-cell [matTooltip]="getMarginTooltip(row)" matTooltipClass="financial-tooltip">
                <strong [ngClass]="getMarginClass(row)">
                    {{ getMarginPercent(row) | number:'1.0-0' }}%
                </strong>
            </td>
        </ng-container>

        <ng-container matColumnDef="marginTotal">
            <th *matHeaderCellDef mat-header-cell mat-sort-header>Margin €</th>
            <td *matCellDef="let row" mat-cell [matTooltip]="getMarginTooltip(row)" matTooltipClass="financial-tooltip">
                <strong [style.color]="(row.totalIncome - row.totalCosts) >= 0 ? '#26b305' : '#e24600'">
                    {{ row.totalIncome - row.totalCosts | currency:'EUR':'symbol':'1.0-0' }}
                </strong>
            </td>
        </ng-container>

        <ng-container matColumnDef="totalIncome">
            <th *matHeaderCellDef mat-header-cell mat-sort-header>Income</th>
            <td *matCellDef="let row" mat-cell [matTooltip]="getIncomeTooltip(row)" matTooltipClass="financial-tooltip">
                {{ row.totalIncome | currency:'EUR':'symbol':'1.0-0' }}
            </td>
        </ng-container>

        <ng-container matColumnDef="totalCosts">
            <th *matHeaderCellDef mat-header-cell mat-sort-header>Costs</th>
            <td *matCellDef="let row" mat-cell [matTooltip]="getCostsTooltip(row)" matTooltipClass="financial-tooltip">
                {{ row.totalCosts | currency:'EUR':'symbol':'1.0-0' }}
            </td>
        </ng-container>

        <tr *matHeaderRowDef="displayedColumns" mat-header-row></tr>
        <tr (click)="openEmployeeDetailModal(row)" *matRowDef="let row; columns: displayedColumns"
            class="pointer" mat-row></tr>

        <tr *matNoDataRow>
            <td [attr.colspan]="displayedColumns.length" class="mat-cell" style="text-align: center; padding: 4rem 2rem;">
                No employees with financial data found for this year.
            </td>
        </tr>
    </table>
</div>
